import { Clock, CheckCircle, XCircle, AlertTriangle, DollarSign, Mail } from 'lucide-react';

const RefundPage = () => {
  const policies = [
    {
      icon: Clock,
      title: '30-Day Money-Back Guarantee',
      description: 'Full refund available within 30 days of purchase',
      details: [
        'Request refund within 30 days of purchase date',
        'Applies to all individual courses and course bundles',
        'No questions asked - we want you to be satisfied',
        'Refund processing typically takes 3-5 business days'
      ]
    },
    {
      icon: CheckCircle,
      title: 'Eligible Refund Requests',
      description: 'Situations where refunds are automatically approved',
      details: [
        'Technical issues preventing course access',
        'Course content significantly different from description',
        'Duplicate purchases or accidental enrollments',
        'Dissatisfaction with course quality or content'
      ]
    },
    {
      icon: XCircle,
      title: 'Refund Limitations',
      description: 'Conditions that may affect refund eligibility',
      details: [
        'Requests made after 30-day period',
        'Courses completed with certificate issued',
        'Violation of our terms of service',
        'Abuse of refund policy (excessive requests)'
      ]
    },
    {
      icon: DollarSign,
      title: 'Refund Process',
      description: 'How to request and receive your refund',
      details: [
        'Contact support with your order details',
        'Provide reason for refund request',
        'Refund processed to original payment method',
        'Email confirmation sent when refund is complete'
      ]
    }
  ];

  const steps = [
    {
      step: '1',
      title: 'Contact Support',
      description: 'Email us at karthik@techgeek.co.in with your refund request'
    },
    {
      step: '2',
      title: 'Provide Details',
      description: 'Include your order number and reason for the refund'
    },
    {
      step: '3',
      title: 'Review Process',
      description: 'We will review your request within 24 hours'
    },
    {
      step: '4',
      title: 'Refund Issued',
      description: 'Approved refunds are processed within 3-5 business days'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-green-500 via-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-green-100 to-emerald-100 bg-clip-text text-transparent">Refund</span> Policy
            </h1>
            <p className="text-xl text-green-50 max-w-3xl mx-auto">
              We stand behind our courses with a 30-day money-back guarantee. Your satisfaction is our priority.
            </p>
          </div>
        </div>
      </div>

      {/* Guarantee Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" aria-label="CheckCircle icon" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              100% <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Satisfaction Guaranteed</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Try any course risk-free for 30 days. If you're not completely satisfied, 
              we'll refund your money - no questions asked.
            </p>
          </div>
        </div>
      </div>

      {/* Policy Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {policies.map((policy, index) => {
            const Icon = policy.icon;
            return (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <div className="flex items-center mb-6">
                  <div className="p-3 bg-green-100 rounded-xl mr-4">
                    <Icon className="h-6 w-6 text-green-600" aria-label="Icon icon" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{policy.title}</h3>
                    <p className="text-gray-600">{policy.description}</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {policy.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-600">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Refund Process */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            How to Request a <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Refund</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 mb-12">
          <div className="flex items-start">
            <AlertTriangle className="h-6 w-6 text-amber-600 mt-1 mr-4 flex-shrink-0" aria-label="AlertTriangle icon" />
            <div>
              <h3 className="text-lg font-bold text-amber-900 mb-4">Important Notes</h3>
              <ul className="space-y-2 text-amber-800">
                <li>• Refunds are processed to the original payment method used for purchase</li>
                <li>• Processing time may vary depending on your bank or payment provider</li>
                <li>• Course access will be revoked once the refund is approved</li>
                <li>• Certificates earned before refund request will be invalidated</li>
                <li>• Bundle refunds include all courses in the purchased bundle</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Need Help with a Refund?</h3>
          <p className="text-green-100 mb-6 max-w-2xl mx-auto">
            Our support team is here to help. Contact us with any questions about our refund policy or to request a refund.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:karthik@techgeek.co.in"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-green-600 font-semibold rounded-xl hover:bg-green-50 transition-colors"
            >
              <Mail className="h-5 w-5 mr-2" aria-label="Mail icon" />
              Email Support
            </a>
            <a 
              href="/contact"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-white font-semibold rounded-xl hover:bg-white hover:text-green-600 transition-colors"
            >
              Contact Form
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPage;